import React, { useState, useEffect, useRef } from 'react';
import { Line } from 'react-chartjs-2';
import 'chart.js/auto';
import { Chart, registerables } from 'chart.js';
import { InputTextarea } from 'primereact/inputtextarea';
// import 'chartjs-adapter-date-fns';

Chart.register(...registerables);

const TemperatureTracker = () => {
    const [temperatureData, setTemperatureData] = useState([
        { date: '2023-01-01', temp: 20, hum: 60 },
        { date: '2023-02-02', temp: 22, hum: 62 },
        { date: '2023-03-03', temp: 21, hum: 61 },
        { date: '2023-04-04', temp: 24, hum: 65 },
        { date: '2023-05-05', temp: 23, hum: 67 },
        { date: '2023-06-07', temp: 25, hum: 68 },
        { date: '2023-07-07', temp: 19, hum: 70 },
        { date: '2023-08-07', temp: -20, hum: 85 },
        { date: '2023-09-05', temp: 23, hum: 45 },
        { date: '2023-10-06', temp: 25, hum: 68 },
        { date: '2023-11-07', temp: 19, hum: 92 },
        { date: '2023-12-07', temp: 30, hum: 86 },
        { date: '2024-01-25', temp: 26, hum: 79 },
    ]);

    const [filteredData, setFilteredData] = useState(temperatureData);
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [comments, setComments] = useState('');
    const [selectedPeriod, setSelectedPeriod] = useState("Aujourd'hui");
    const [commentList, setCommentList] = useState([]);
    const [selectedPoint, setSelectedPoint] = useState(null);
    const [highlightedPointIndex, setHighlightedPointIndex] = useState(null);
    const chartRef = useRef(null);
    const chartContainerRef = useRef(null); // Référence au conteneur du graphique

    useEffect(() => {
        const savedComments = JSON.parse(localStorage.getItem('comments')) || [];
        setCommentList(savedComments);
    }, []);

    const filterDataByPeriod = (period) => {
        let today = new Date();
        let start, end;

        switch (period) {
            case "Aujourd'hui":
                start = end = today;
                break;
            case "Hier":
                start = end = new Date(today.setDate(today.getDate() - 1));
                break;
            case "3 derniers jours":
                start = new Date(today.setDate(today.getDate() - 3));
                end = new Date();
                break;
            case "7 derniers jours":
                start = new Date(today.setDate(today.getDate() - 7));
                end = new Date();
                break;
            case "30 derniers jours":
                start = new Date(today.setDate(today.getDate() - 30));
                end = new Date();
                break;
            case "Année dernière":
                start = new Date(today.setDate(today.getDate() - 365));
                end = new Date();
                break;
            default:
                return;
        }

        setStartDate(start);
        setEndDate(end);
        filterData(start, end);
    };

    const filterData = (start, end) => {
        let filtered = temperatureData;
        if (start) {
            filtered = filtered.filter(data => new Date(data.date) >= start);
        }
        if (end) {
            filtered = filtered.filter(data => new Date(data.date) <= end);
        }
        setFilteredData(filtered);
    };

    const data = {
        labels: filteredData.map(d => d.date),
        datasets: [
            {
                label: 'Température (°C)',
                data: filteredData.map(d => d.temp),
                borderColor: 'rgba(30, 192, 192, 1)',
                backgroundColor: 'rgba(75, 192, 192, 0.2)',
                borderWidth: 2,
                pointBackgroundColor: filteredData.map((_, i) =>
                    i === highlightedPointIndex ? 'rgba(255, 255, 255, 1)' : 'rgba(75, 192, 192, 0.2)'
                ),
                pointBorderColor: filteredData.map((_, i) =>
                    i === highlightedPointIndex ? 'rgba(0, 0, 255, 1)' : 'rgba(75, 192, 192, 0.2)'
                ),
                pointRadius: filteredData.map((_, i) =>
                    i === highlightedPointIndex ? 6 : 3
                ),
            },
            {
                label: 'Humidité (%)',
                data: filteredData.map(d => d.hum),
                borderColor: 'rgba(255, 99, 132, 1)',
                backgroundColor: 'rgba(255, 99, 132, 0.2)',
                borderWidth: 2,
                pointBackgroundColor: filteredData.map((_, i) =>
                    i === highlightedPointIndex ? 'rgba(255, 255, 255, 1)' : 'rgba(255, 99, 132, 0.2)'
                ),
                pointBorderColor: filteredData.map((_, i) =>
                    i === highlightedPointIndex ? 'rgba(0, 0, 255, 1)' : 'rgba(255, 99, 132, 0.2)'
                ),
                pointRadius: filteredData.map((_, i) =>
                    i === highlightedPointIndex ? 6 : 3
                ),
            },
        ],
    };

    const handlePointClick = (event) => {
        const chart = chartRef.current;
        const elements = chart.getElementsAtEventForMode(event, 'nearest', { intersect: true }, false);
        if (elements.length) {
            const index = elements[0].index;
            setSelectedPoint(filteredData[index]);
            highlightPoint(index);
        } else {
            setSelectedPoint(null);
        }
    };

    const handleAddComment = () => {
        if (comments.trim() !== '' && selectedPoint) {
            const newComment = {
                period: `${startDate.toISOString().slice(0, 10)} - ${endDate.toISOString().slice(0, 10)}`,
                temperature: selectedPoint.temp,
                humidity: selectedPoint.hum,
                text: comments,
                date: new Date().toISOString(),
                isLinkedToPoint: true,
                pointIndex: filteredData.findIndex(data => data.temp === selectedPoint.temp && data.hum === selectedPoint.hum), // Sauvegarder l'index du point dans filteredData
            };
            const updatedComments = [...commentList, newComment];
            setCommentList(updatedComments);
            localStorage.setItem('comments', JSON.stringify(updatedComments));
            setComments('');
            setStartDate(null);
            setEndDate(null);
            setSelectedPoint(null);
        }
    };

    const highlightPoint = (index) => {
        setHighlightedPointIndex(index);
    };

    const handleCommentHighlight = (index) => {
        const comment = commentList[index];
        if (comment.pointIndex !== undefined && filteredData[comment.pointIndex]) {
            highlightPoint(comment.pointIndex);
            setSelectedPoint(filteredData[comment.pointIndex]);
            chartContainerRef.current.scrollIntoView({ behavior: 'smooth' }); // Scroll vers le graphique
        }
    };

    return (
        <div className="max-w-2xl mx-auto p-5 bg-white shadow-lg rounded-lg">
            <h1 className="text-2xl font-bold mb-4 text-center">Suivi de Température des Engins</h1>

            {/* Période de filtration */}
            <div className="flex mb-4 space-x-4 gap-2">
                <div className="flex-1">
                    <label className="block text-sm font-medium mb-1">Sélectionnez la période:</label>
                    <select
                        value={selectedPeriod}
                        onChange={(e) => {
                            setSelectedPeriod(e.target.value);
                            filterDataByPeriod(e.target.value);
                        }}
                        className="w-full h-10 p-2 border border-gray-300 rounded-md bg-white text-sm"
                    >
                        <option value="Aujourd'hui">Aujourd'hui</option>
                        <option value="Hier">Hier</option>
                        <option value="3 derniers jours">3 derniers jours</option>
                        <option value="7 derniers jours">7 derniers jours</option>
                        <option value="30 derniers jours">30 derniers jours</option>
                        <option value="Année dernière">Année dernière</option>
                    </select>
                </div>
                <div className="flex-1">
                    <label className="block text-sm font-medium mb-1">Date de début:</label>
                    <input
                        type="date"
                        value={startDate?.toISOString().slice(0, 10) || ""}
                        onChange={(e) => {
                            setStartDate(new Date(e.target.value));
                            filterData(new Date(e.target.value), endDate);
                        }}
                        className="w-full h-10 p-2 border border-gray-300 rounded-md bg-white text-sm"
                    />
                </div>

                <div className="flex-1">
                    <label className="block text-sm font-medium mb-1">Date de fin:</label>
                    <input
                        type="date"
                        value={endDate?.toISOString().slice(0, 10) || ""}
                        onChange={(e) => {
                            setEndDate(new Date(e.target.value));
                            filterData(startDate, new Date(e.target.value));
                        }}
                        className="w-full h-10 p-2 border border-gray-300 rounded-md bg-white text-sm"
                    />
                </div>
            </div>

            {/* Graphique */}
            <div className="chart-container mb-4" ref={chartContainerRef} onClick={handlePointClick}>
                <Line
                    data={data}
                    options={{
                        scales: {
                            x: {
                                type: 'time',
                                time: {
                                    unit: 'day',
                                },
                            },
                        },
                    }}
                    ref={chartRef}
                />
            </div>

            {/* Détails du point sélectionné */}
            {selectedPoint && (
                <div className="bg-white border border-gray-300 rounded-md p-4 mb-4">
                    <p>Date: {selectedPoint.date}</p>
                    <p>Température: {selectedPoint.temp} °C</p>
                    <p>Humidité: {selectedPoint.hum} %</p>
                    <div className="flex mb-4">

                        <div className="flex-1">
                            <label className="block text-sm font-medium mb-1">Date de début:</label>
                            <input
                                type="date"
                                value={startDate?.toISOString().slice(0, 10) || ''}
                                onChange={(e) => setStartDate(new Date(e.target.value))}
                                className="w-full h-10 p-2 border border-gray-300 rounded-md"
                            />
                        </div>
                        <div className="flex-1">
                            <label className="block text-sm font-medium mb-1">Date de fin:</label>
                            <input
                                type="date"
                                value={endDate?.toISOString().slice(0, 10) || ''}
                                onChange={(e) => setEndDate(new Date(e.target.value))}
                                className="w-full h-10 p-2 border border-gray-300 rounded-md"
                            />
                        </div>

                    </div>
                    <div className="mb-4">
                        <InputTextarea
                            value={comments}
                            onChange={(e) => setComments(e.target.value)}
                            placeholder="Ajoutez vos commentaires ici..."
                            rows={5}
                            className="w-full border border-gray-300 rounded-md p-2"
                        />
                    </div>
                    <button
                        onClick={handleAddComment}
                        className="bg-blue-500 text-white p-2 rounded-md hover:bg-blue-600"
                    >
                        Commenter
                    </button>
                </div>
            )}

            {/* Liste des commentaires */}
            <div className="mt-6">
                <h2 className="text-2xl font-bold mb-6 text-gray-800">Commentaires</h2>
                <div className="bg-gray-50 border border-gray-300 rounded-lg p-6 shadow-lg">
                    {commentList.sort((a, b) => new Date(b.date) - new Date(a.date)).map((comment, index) => (
                        <div key={index} className={`p-4 mb-5 rounded-lg border border-gray-200 shadow-md`}>
                            <div className="flex items-center mb-1">
                                <i className="fas fa-calendar-alt text-blue-500 mr-2"></i>
                                <span className="font-semibold text-gray-700">{comment.period}</span>
                                <span className="ml-auto flex items-center">
                                    <i className="fas fa-clock text-blue-500 mr-2"></i>
                                    <span className="text-sm text-gray-500">{new Date(comment.date).toLocaleString()}</span>
                                </span>
                            </div>
                            <div className="mb-3 text-gray-700">
                                <i className="fas fa-comment-dots text-blue-500 mr-2"></i>
                                <strong className="text-gray-800">Commentaire </strong>
                                <p className="mt-1 overflow-hidden break-words">{comment.text}</p>
                            </div>
                            <div className="flex justify-between gap-3">
                                <div className="text-gray-700">
                                    <strong>Température :</strong> {comment.temperature} °C
                                </div>
                                <div className="text-gray-700">
                                    <strong>Humidité :</strong> {comment.humidity} %
                                </div>
                                <button
                                    onClick={() => {
                                        handleCommentHighlight(index);
                                        chartContainerRef.current.scrollIntoView({ behavior: 'smooth' }); // Scroll vers le graphique
                                    }}
                                    className="bg-blue-500 text-white p-2 ml-2 rounded-md hover:bg-blue-600"
                                >
                                    Voir sur le graphique
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default TemperatureTracker;